<template>
    <v-row>
        <v-col cols="12">
            <back-button/>
        </v-col>
        <v-col cols="12" v-if="loading">
            <loading-spinner/>
        </v-col>
        <template v-else>
            <v-col cols="12" md="4">
                <v-row>
                    <v-col cols="6" v-if="studentImg">
                        <v-img :src="studentImg" max-height="180" contain/>
                    </v-col>
                    <v-col :cols="studentImg ? 6 : 12">
                        <div v-for="info in studentInfo">
                            <v-chip label small class="mb-1" outlined color="black" style="white-space: normal; height: auto">
                                {{ info.text }}: {{ info.value }}
                            </v-chip>
                        </div>
                    </v-col>
                </v-row>
            </v-col>
            <v-col cols="12" md="8" v-if="!hasValues">
                <v-alert color="error" dark outlined icon="mdi-alert-circle-outline">
                    {{ $t('common.no_values_student') }}
                </v-alert>
            </v-col>
            <template v-else>
                <v-col cols="12" md="8">
                    <v-data-table
                        :headers="headers"
                        :loading="loading"
                        :items="items"
                        :mobile-breakpoint="0"
                        hide-default-footer
                        :loading-text="$t('common.loading')"
                        :no-data-text="$t('common.not_found')"
                    >
                        <template v-slot:item.difference="{ item }">
                    <span :class="`${item.color}--text bolder`">
                        {{ item.difference }}
                    </span>
                        </template>
                    </v-data-table>
                </v-col>
                <v-col cols="12">
                    <v-row class="mt-3">
                        <v-col cols="12" md="6" lg="4" offset-lg="2">
                            <div class="chart-container">
                                <bar-line-evaluation-chart
                                    v-if="currentValues"
                                    :best="currentValues.best"
                                    :worst="currentValues.worst"
                                    :average="currentValues.average"
                                    :actual="currentValues.actual"
                                    :difference="currentValues.difference"
                                />
                                <kimsa-combobox
                                    :placeholder="$t('inputs.evaluations_placeholder')"
                                    outlined dense solo
                                    :items="config"
                                    @change="currentConfig = $event"
                                    :value="currentConfig"
                                />
                            </div>
                        </v-col>
                        <v-col cols="12" md="6" lg="4">
                            <radar-chart
                                class="chart-container pt-10"
                                :labels="evaluationsName"
                                :datasets="evaluationData"
                            />
                        </v-col>
                    </v-row>
                </v-col>
            </template>
        </template>
    </v-row>
</template>

<script>
import BackButton from "@/components/common/BackButton";
import {db} from "@/utils/Firebase";
import {mapActions, mapState} from "vuex";
import KimsaCombobox from "@/components/common/inputs/KimsaCombobox";
import BarLineEvaluationChart from "@/components/common/BarLineEvaluationChart";
import LoadingSpinner from "@/components/common/LoadingSpinner";
import RadarChart from "@/components/common/RadarChart";

export default {
    name: "EvaluationStatisticsStudent",
    components: {RadarChart, LoadingSpinner, BarLineEvaluationChart, KimsaCombobox, BackButton},
    data(vm) {
        return {
            loading: true,
            student: null,
            assignedEvaluation: null,
            headers: [
                {text: '', value: 'name', align: 'center'},
                {text: '', value: 'unit', align: 'center'},
                {text: vm.$t('common.actual'), value: 'actual', align: 'center'},
                {text: vm.$t('common.difference_ideal'), value: 'difference', align: 'center'},
                {text: vm.$t('common.best'), value: 'best', align: 'center'},
                {text: vm.$t('common.worst'), value: 'worst', align: 'center'},
                {text: vm.$t('common.average'), value: 'average', align: 'center'},
            ],
            currentConfig: null,
        }
    },
    computed: {
        ...mapState(['evaluations']),
        hasValues(vm = this) {
            return vm.assignedEvaluation?.values || false
        },
        studentId(vm = this) {
            return vm.$route.params.id
        },
        evaluationId(vm = this) {
            return vm.assignedEvaluation?.evaluationId || null
        },
        currentEvaluation(vm = this) {
            if (!vm.evaluationId) return null
            return vm.evaluations.find(eva => eva.id === vm.evaluationId) || null
        },
        config(vm = this) {
            return vm.currentEvaluation?.config || []
        },
        values(vm = this) {
            if (!vm.hasValues) return []
            const values = {}
            vm.assignedEvaluation.values?.forEach(item => {
                for (const key in item) {
                    if (key !== "date") {
                        const value = parseFloat(item[key])
                        if (!values[key]) values[key] = {best: -Infinity, worst: Infinity, sum: 0, count: 0}
                        values[key].sum += value
                        values[key].count++
                        values[key].best = Math.max(values[key].best, value)
                        values[key].worst = Math.min(values[key].worst, value)
                        values[key].actual = value
                        values[key].all = [...values[key].all || [], value]
                    }
                }
            })

            for (const key in values) {
                const average = values[key].average = values[key].sum / values[key].count
                values[key].average = average % 1 === 0 ? average.toFixed(0) : average.toFixed(2)
            }
            return values
        },
        items(vm = this) {
            if (!vm.config || !vm.values) return []

            let items = []
            vm.config.forEach((config, i) => {

                let currentValue = vm.values[config.id]
                let actual = currentValue.actual
                let ideal = parseFloat(config.value)
                let color = config.upgrade ? (actual > ideal ? 'success' : 'error') : (actual > ideal ? 'error' : 'success')
                let difference = ((actual * 100) / ideal) - 100
                if (difference < 0) difference = difference * -1
                difference = difference % 1 === 0 ? difference.toFixed(0) : difference.toFixed(2)

                let payload = {
                    id: config.id,
                    name: config.name,
                    unit: vm.$t(`units.${config.unit}_short`),
                    actual: actual,
                    difference: difference,
                    color: color,
                    best: config.upgrade ? currentValue.best : currentValue.worst,
                    worst: config.upgrade ? currentValue.worst : currentValue.best,
                    average: currentValue.average,
                    ideal: ideal,
                    upgrade: config.upgrade,
                }

                items.push(payload)
            })

            return items
        },
        currentValues(vm = this) {
            if (!vm.currentConfig) return null
            let values = vm.values[vm.currentConfig.id]
            let difference = ((values.actual * 100) / vm.currentConfig.value) - 100
            if (difference < 0) difference = difference * -1
            difference = difference % 1 === 0 ? difference.toFixed(0) : difference.toFixed(2)
            return {
                difference: difference,
                ...values
            }
        },
        evaluationsName(vm = this) {
            if (!vm.config) return []
            return vm.config.map(c => c.name)
        },
        evaluationData(vm = this) {
            if (vm.items.length === 0) return []
            let ideal = []
            let actual = []
            let idealReal = []
            let actualReal = []
            vm.items.forEach(item => {
                ideal.push(3)
                actual.push(vm.getStandardDeviation(item.actual, item.average, vm.values[item.id].all))
                idealReal.push(item.ideal)
                actualReal.push(item.actual)
            })

            return [
                {
                    label: vm.$t('common.ideal'),
                    backgroundColor: 'rgba(204,255,153,0.2)',
                    borderColor: 'rgba(204,255,153,1)',
                    pointBackgroundColor: 'rgba(204,255,153,1)',
                    pointBorderColor: 'rgba(204,255,153,1)',
                    pointHoverBackgroundColor: 'rgba(204,255,153,1)',
                    pointHoverBorderColor: 'rgba(204,255,153,1)',
                    data: ideal,
                    dataReal: idealReal,
                },
                {
                    label: vm.$t('common.actual'),
                    backgroundColor: 'rgba(0,0,0,0.2)',
                    borderColor: 'rgba(0,0,0,1)',
                    pointBackgroundColor: 'rgba(0,0,0,1)',
                    pointBorderColor: 'rgba(0,0,0,1)',
                    pointHoverBackgroundColor: 'rgba(0,0,0,1)',
                    pointHoverBorderColor: 'rgba(0,0,0,1)',
                    data: actual,
                    dataReal: actualReal,
                }
            ]
        },
        studentImg(vm = this) {
            return vm.student?.avatar || null
        },
        studentInfo(vm = this) {
            let studentAge = vm.$t('common.not_available_short')
            if (vm.student.birthDate) studentAge = vm.$moment().diff(vm.$moment(vm.student.birthDate.seconds * 1000), 'years')
            return [
                {text: vm.$t('common.name'), value: vm.student.name},
                {text: vm.$t('common.sport'), value: vm.student?.sportSelected || vm.$t('common.not_available_short')},
                {text: vm.$t('common.age'), value: studentAge},
                {text: vm.$t('common.height'), value: vm.student?.height || vm.$t('common.not_available_short')},
                {text: vm.$t('common.weight'), value: vm.student?.weight || vm.$t('common.not_available_short')},
            ]
        }
    },
    async mounted() {
        const vm = this
        if (vm.evaluations.length === 0) await vm.bindEvaluations(vm.currentUser.uid)
        await vm.loadAssignedEvaluation()
    },
    methods: {
        ...mapActions(['bindEvaluations']),
        async loadAssignedEvaluation() {
            const vm = this
            vm.loading = true

            let ref = await db.collection('users').doc(vm.studentId).get()
            vm.student = {id: ref.id, ...ref.data()}

            let assignedRef = await db.collection('assignedEvaluations').doc(vm.student.assignedEvaluationId).get()
            vm.assignedEvaluation = {id: assignedRef.id, ...assignedRef.data()}

            vm.loading = false
        },
        getStandardDeviation(value, average, data) {
            const mean = data.reduce((acc, x) => acc + x, 0) / data.length
            const squaredDifferences = data.map(x => Math.pow(x - mean, 2))
            const variance = squaredDifferences.reduce((acc, x) => acc + x, 0) / data.length
            const standardDeviation = Math.sqrt(variance)
            let z = (value - average) / standardDeviation
            if (z < 0) z = z * -1
            return z
        }
    },
    watch: {
        config(val) {
            if (val && val[0]) this.currentConfig = val[0]
        }
    }
}
</script>

<style scoped>

>>> .v-data-table-header > tr {
    box-shadow: 3px 3px 6px 0 rgba(1, 55, 46, 0.1) !important;
}

>>> tbody > tr {
    box-shadow: 3px 3px 6px 0 rgba(1, 55, 46, 0.1) !important;
}

>>> tbody > tr > td {
    border: none !important;
    padding-top: 8px !important;
    padding-bottom: 4px !important;
}

>>> .v-data-table__wrapper {
    overflow: visible;
}

>>> .v-data-table-header > tr > th {
    border: none !important;
}

>>> .v-data-table-header > tr > th > span {
    font-weight: bold !important;
}

.chart-container {
    /*border: 1px solid #666666;*/
    box-shadow: 3px 3px 6px 0 rgba(1, 55, 46, 0.1) !important;
    border-radius: 4px;
}

</style>